import { ComponentType, MouseEventHandler } from 'react';

import { BookMarkedIcon, BrainCircuitIcon, LocateFixedIcon, ShieldIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import useMcpServers from '@/components/pages/MCPs/hooks/useMcpServers';
import useCollections from '@/hooks/useCollections';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';

import { useGoToDialogNode } from '../hooks/useGoToDialogNode';
import { selectBrainId, selectIsReplay } from '../redux/selectors';
import { ConversationTurn } from '../types/conversation';

type IconComponent = ComponentType<{ size?: number; className?: string }>;

export type TurnSource = {
  icon: IconComponent;
  name: string;
  link?: {
    href: string;
    tooltip: string;
    external?: boolean;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
  };
};

const GROUP_CLASS = 'flex items-center justify-center gap-1 p-0 text-foreground-muted';
// Named: the turn row wraps this in a bare `group`, which would drive `group-hover:`.
const LINK_CLASS = `group/source ${GROUP_CLASS} cursor-pointer hover:text-informational`;

const nodeNameOf = (turn: ConversationTurn) =>
  turn.metadata.nodesStack?.[0]?.name ?? turn.metadata.triggeredNode?.name;

const isKnowledgeBaseTurn = (turn: ConversationTurn) =>
  turn.category.kind === 'knowledge_base' &&
  Boolean(turn.metadata.knowledgeBase?.id ?? turn.metadata.collection?.collection_id);

const ANSWERED_BY_MODEL: ConversationTurn['category']['kind'][] = [
  'guidelines_llm',
  'mcp',
  'unknown',
];

export const useTurnSource = (turn: ConversationTurn): TurnSource | null => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const isReplay = useSelector(selectIsReplay);

  const nodeId = turn.metadata.nodesStack?.[0]?.node_id ?? turn.metadata.triggeredNode?.id;
  const nodeLink = useGoToDialogNode(nodeId);

  const collectionId = turn.metadata.knowledgeBase?.id ?? turn.metadata.collection?.collection_id;
  const { collection } = useCollections(collectionId);

  const brainId = useSelector(selectBrainId);
  const { mcpServers, listStatus } = useMcpServers(brainId);

  if (isKnowledgeBaseTurn(turn)) {
    const name = collection?.name || turn.metadata.knowledgeBase?.name;
    // Unlinked: a collection we could not read may no longer exist.
    if (!name) {
      return { icon: BookMarkedIcon, name: t('collections.collection') };
    }
    if (!slug || !collectionId) {
      return { icon: BookMarkedIcon, name };
    }
    return {
      icon: BookMarkedIcon,
      name,
      link: {
        href: `/${slug}/knowledge-bases/${collectionId}`,
        tooltip: t('try_it.go_to_knowledge_base'),
        external: true,
      },
    };
  }

  if (!isReplay && turn.metadata.isAuthentication) {
    const name = t('common.authentication');
    if (!nodeLink) {
      return { icon: ShieldIcon, name };
    }
    return {
      icon: ShieldIcon,
      name,
      link: { href: nodeLink.to, tooltip: t('try_it.go_to_dialog'), external: true },
    };
  }

  const nodeName = nodeNameOf(turn);
  if (!nodeName) {
    if (!ANSWERED_BY_MODEL.includes(turn.category.kind)) {
      return null;
    }
    const toolCalls = turn.metadata.toolCalls ?? [];
    // Only a configured server counts: Moveo's internal calls run on every LLM answer.
    if (toolCalls.length > 0 && listStatus === 'pending') {
      return null;
    }
    const usedTool = toolCalls.some((tool) =>
      mcpServers.some((server) => server.mcp_id === tool.mcp_id)
    );
    return usedTool
      ? { icon: McpIcon, name: t('try_it.category.tools') }
      : { icon: BrainCircuitIcon, name: t('try_it.category.llm') };
  }

  if (!nodeLink) {
    return { icon: LocateFixedIcon, name: nodeName };
  }

  return {
    icon: LocateFixedIcon,
    name: nodeName,
    link: { href: nodeLink.to, tooltip: t('try_it.go_to_dialog'), external: true },
  };
};

type TurnSourceLabelProps = {
  source: TurnSource;
  /** `row`: 12px and plain text — only the panel links the source. */
  variant?: 'panel' | 'row';
};

export const TurnSourceLabel = ({ source, variant = 'panel' }: TurnSourceLabelProps) => {
  const isRow = variant === 'row';
  const { icon: Icon, name, link } = source;
  const content = (
    <>
      <Icon size={isRow ? 14 : 16} className="shrink-0" />
      <span
        className={cn(
          'font-medium group-hover/source:underline',
          isRow ? 'text-xs leading-4' : 'text-sm leading-none'
        )}
      >
        {name}
      </span>
    </>
  );

  if (isRow || !link) {
    return <div className={GROUP_CLASS}>{content}</div>;
  }

  return (
    <InfoTooltip content={link.tooltip} side="bottom">
      <a
        href={link.href}
        onClick={link.onClick}
        target={link.external ? '_blank' : undefined}
        rel={link.external ? 'noopener noreferrer' : undefined}
        className={LINK_CLASS}
      >
        {content}
      </a>
    </InfoTooltip>
  );
};

export const TurnJumpLabel = ({ nodeId, name }: { nodeId: string; name: string }) => {
  const { t } = useTranslation();
  const nodeLink = useGoToDialogNode(nodeId);

  const source: TurnSource = {
    icon: LocateFixedIcon,
    name,
    link: nodeLink
      ? { href: nodeLink.to, tooltip: t('try_it.go_to_dialog'), external: true }
      : undefined,
  };

  return <TurnSourceLabel source={source} />;
};
